import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Edit } from '@mui/icons-material';
import { Button, Checkbox, TextField } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import * as S from 'commons/components/Table/Table.styles';
import { PRIMARY_RED, PRIMARY_YELLOW, LIGHT_GRAY } from 'commons/styles/colors';
import { DataTableProps } from 'commons/types/basketInfoData.interface';
import { BasketProductDetails } from 'commons/types/packingTask.interface';
import {
  checkItemAction,
  updateQtyChecked,
} from 'redux-stores/actions/packingTask.action';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { CheckBasketStyle as SB } from '../CheckBasket.styles';

import { BasketInfoDataTable } from './BasketInfoDataTable.component';
import EditQtyModal from './EditQtyModal.component';

const Row: React.FC<{
  data: BasketProductDetails;
  onCheckboxClick: (itemIndex: number) => void;
  itemIndex: number;
  basketNumber: number;
  getTotalQtyBySkuCode: (skuCode: string) => number;
  isDisabled: boolean;
}> = ({
  data,
  onCheckboxClick,
  itemIndex,
  basketNumber,
  getTotalQtyBySkuCode,
  isDisabled,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { packingTask } = useSelector(
    (state: RootReducerInterface) => state.packingTask,
  );
  const [open, setOpen] = useState<boolean>(false);
  const [qtyChecked, setQtyChecked] = useState<number | null>(
    data.total_qty_checked || null,
  );
  const [qtyCheckInput, setQtyCheckInput] = useState<string | null>(null);

  const handleOpenDialog = (): void => {
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpen(false);
  };

  const debouncedChangeQtyChecked = useDebouncedCallback(
    async (value: string) => {
      let newQtyChecked = qtyChecked || 0;

      if (value === '') {
        newQtyChecked = 0;
        // } else if (data.barcode && value.includes(data.barcode)) {
        //   newQtyChecked += 1;
      } else if (!Number.isNaN(+value) && +value <= 9999) {
        newQtyChecked = +value > 0 ? Math.floor(+value) : 0;
      }

      setQtyChecked(newQtyChecked);
      setQtyCheckInput(null);
      dispatch(
        updateQtyChecked({
          poNumber: packingTask?.po_number || '',
          payload: {
            ...data,
            total_qty_checked: newQtyChecked,
            check_passed:
              newQtyChecked === data.total_qty_picked &&
              newQtyChecked === data.quantity_ordered
                ? true
                : data.check_passed,
            basket: basketNumber,
          },
        }),
      );
    },
    500,
    { maxWait: 2000, trailing: true },
  );

  const onChangeQtyChecked = (value: string) => {
    setQtyCheckInput(value);
    debouncedChangeQtyChecked(value);
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn>
          <SB.BoldText>{data.sku_code}</SB.BoldText>
          {data.is_modified ? (
            <SB.GreenText data-testid="labelCheckModified">
              Modified
            </SB.GreenText>
          ) : null}
        </S.TableColumn>
        <S.TableColumn>{data.name}</S.TableColumn>
        <S.TableColumn>{data.uom}</S.TableColumn>
        {/* <S.TableColumn>{data.barcode ? 'Yes' : 'No'}</S.TableColumn> */}
        <S.TableColumn style={{ textAlign: 'center' }}>
          {data.quantity_ordered}
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center' }}>
          <SB.QtyPicked
            style={{
              borderColor:
                getTotalQtyBySkuCode(data.sku_code) > data.quantity_ordered ||
                data.total_qty_picked === 0
                  ? PRIMARY_RED
                  : getTotalQtyBySkuCode(data.sku_code) < data.quantity_ordered
                  ? PRIMARY_YELLOW
                  : LIGHT_GRAY,
            }}
            disabled
            data-testid="BtnCheckQTYPicked"
          >
            {data.total_qty_picked}
          </SB.QtyPicked>
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center', width: '69px' }}>
          <TextField
            variant="outlined"
            type="number"
            value={
              qtyCheckInput !== null
                ? qtyCheckInput
                : qtyChecked
                ? qtyChecked.toString()
                : ''
            }
            onChange={(event): void => onChangeQtyChecked(event.target.value)}
            disabled={isDisabled}
            data-testid="CheckedQTYField"
          />
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center' }}>
          <Checkbox
            onClick={() => onCheckboxClick(itemIndex)}
            checked={data.check_passed}
            disabled={
              getTotalQtyBySkuCode(data.sku_code) > data.quantity_ordered ||
              isDisabled
            }
            inputProps={
              {
                'data-testid': `checkPassed-${basketNumber}-${itemIndex + 1}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
          <SB.WarningText
            style={{
              visibility: data.total_qty_picked === 0 ? 'visible' : 'hidden',
            }}
          >
            (Please delete/modify on netsuite)
          </SB.WarningText>
        </S.TableColumn>
        <S.TableColumn>
          <Button onClick={() => handleOpenDialog()}>
            <Edit />
          </Button>
        </S.TableColumn>
      </S.TableRow>
      <EditQtyModal
        openModal={open}
        onClose={() => handleCloseDialog()}
        data={data}
        basketNumber={basketNumber}
      />
    </>
  );
};

export const BasketInfoTable: React.FC<DataTableProps> = (
  params,
): React.ReactNode => {
  const {
    poNumber,
    data = [],
    rowTitles,
    basketIndex,
    basketNumber,
    getTotalQtyBySkuCode,
    isDisabled,
  }: DataTableProps = params;

  const dispatch = useDispatch<AppDispatch>();

  const handleCheckPassed = (itemIndex: number): void => {
    dispatch(
      checkItemAction({
        poNumber,
        basket: basketNumber,
        skuCode: data[itemIndex].sku_code,
        isChecked: !data[itemIndex].check_passed,
        basketIndex,
        itemIndex,
      }),
    );
  };

  return (
    <BasketInfoDataTable rowTitles={rowTitles}>
      <S.TableBody>
        {data.map((item, index) => (
          <Row
            data={item}
            key={item.sku_code}
            onCheckboxClick={handleCheckPassed}
            itemIndex={index}
            getTotalQtyBySkuCode={getTotalQtyBySkuCode}
            basketNumber={basketNumber}
            isDisabled={isDisabled}
          />
        ))}
      </S.TableBody>
    </BasketInfoDataTable>
  );
};
